"use client";

import * as React from "react";
import { cn } from "../../utils/cn";

type TimelineDirection = "vertical" | "horizontal";

interface TimelineContextValue {
  direction: TimelineDirection;
}

const TimelineContext = React.createContext<TimelineContextValue>({
  direction: "vertical",
});

const Timeline = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { direction?: TimelineDirection }
>(({ className, direction = "vertical", ...props }, ref) => {
  const contextValue = React.useMemo(() => ({ direction }), [direction]);

  return (
    <TimelineContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cn(
          "flex w-full relative",
          direction === "vertical" ? "flex-col" : "flex-row justify-between",
          className,
        )}
        {...props}
      />
    </TimelineContext.Provider>
  );
});
Timeline.displayName = "Timeline";

const TimelineItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { direction } = React.useContext(TimelineContext);
    return (
      <div
        ref={ref}
        className={cn(
          "relative flex flex-1",
          direction === "vertical" ? "flex-row items-stretch gap-6" : "flex-col items-center",
          className,
        )}
        {...props}
      />
    );
  },
);
TimelineItem.displayName = "TimelineItem";

const TimelineSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { direction } = React.useContext(TimelineContext);
    return (
      <div
        ref={ref}
        className={cn(
          "flex shrink-0 items-center",
          direction === "vertical" ? "flex-col w-6" : "flex-row h-10 w-full mb-3",
          className,
        )}
        {...props}
      />
    );
  },
);
TimelineSeparator.displayName = "TimelineSeparator";

interface TimelineDotProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: "default" | "active" | "completed" | "inactive" | "error";
}

const TimelineDot = React.forwardRef<HTMLDivElement, TimelineDotProps>(
  ({ className, status = "default", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10 border-4 border-surface-container-lowest",
        {
          "bg-surface-container-high text-on-surface-variant": status === "default",
          "bg-secondary text-secondary-on shadow-[0_0_15px_rgba(var(--secondary),0.4)]":
            status === "active",
          "bg-secondary text-secondary-on": status === "completed",
          "bg-surface-container-highest text-on-surface-variant/50": status === "inactive",
          "bg-error text-error-on shadow-lg ring-4 ring-error/10": status === "error",
        },
        className,
      )}
      {...props}
    />
  ),
);
TimelineDot.displayName = "TimelineDot";

const TimelineConnector = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { active?: boolean }
>(({ className, active, ...props }, ref) => {
  const { direction } = React.useContext(TimelineContext);
  return (
    <div
      ref={ref}
      className={cn(
        "bg-surface-container-high",
        active ? "bg-secondary" : "",
        direction === "vertical" ? "w-0.5 flex-1" : "h-1 flex-1",
        className,
      )}
      {...props}
    />
  );
});
TimelineConnector.displayName = "TimelineConnector";

const TimelineContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { direction } = React.useContext(TimelineContext);
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col flex-1",
          direction === "vertical" ? "pb-8 pt-2" : "items-center text-center",
          className,
        )}
        {...props}
      />
    );
  },
);
TimelineContent.displayName = "TimelineContent";

export {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
};
